import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePreferences } from '@/store/preferences';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import AppleSignInButton from '@/components/auth/AppleSignInButton';
import { track, AnalyticsEvent } from '@/lib/analytics';


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignUp = () => {
  const navigate = useNavigate();
  const { t, language } = usePreferences();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = t.first_name_required || 'First name is required';
    if (!lastName.trim()) errs.lastName = t.last_name_required || 'Last name is required';
    if (!email.trim() || !EMAIL_RE.test(email.trim())) errs.email = language === 'fr' ? 'Adresse email invalide.' : 'Invalid email address.';
    if (password.length < 8) errs.password = language === 'fr' ? 'Au moins 8 caractères.' : 'At least 8 characters.';
    if (!acceptedPrivacy) errs.privacy = language === 'fr' ? 'Tu dois accepter la Politique de confidentialité.' : 'You must accept the Privacy Policy.';
    if (!acceptedTerms) errs.terms = language === 'fr' ? "Tu dois accepter les Conditions Générales d'Utilisation." : 'You must accept the Terms of Use.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    const { error } = await supabase.auth.signUp({
      email: cleanEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/verify-email-confirm`,
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          language,
        },
      },
    });
    setSubmitting(false);

    if (error) {
      const msg = /already registered|exists/i.test(error.message)
        ? (language === 'fr' ? 'Un compte avec cet email existe déjà.' : 'An account with this email already exists.')
        : error.message;
      setErrors({ email: msg });
      return;
    }
    void track(AnalyticsEvent.SIGN_UP, { method: 'email' });
    toast.success(language === 'fr' ? 'Compte créé ! Vérifiez votre email.' : 'Account created! Check your email.');
    navigate('/verify-email', { replace: true });
  };

  const glassInput = 'rounded-xl border-border/60 bg-card/80 py-5 text-foreground placeholder:text-muted-foreground backdrop-blur-sm shadow-[inset_0_1px_2px_hsl(0_0%_0%/0.3)] focus:ring-primary/30 focus:border-primary/40 transition-all duration-200';

  return (
    <div className="relative mx-auto flex min-h-screen max-w-lg flex-col bg-background px-6 overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[280px] w-[400px] rounded-full bg-primary/8 blur-[120px]" />
      <header className="relative z-10 flex items-center gap-3 py-4">
        <button onClick={() => navigate(-1)} className="text-foreground active:scale-90 transition-transform"><ArrowLeft className="h-5 w-5" /></button>
      </header>
      <div className="relative z-10 flex flex-1 flex-col justify-center pb-20">
        <h1 className="text-2xl font-extrabold uppercase tracking-widest text-foreground">{t.sign_up}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t.join_athletes}</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm text-foreground">{t.first_name || 'First Name'}</Label>
              <Input id="firstName" type="text" placeholder={t.first_name || 'First name'} value={firstName} onChange={(e) => { setFirstName(e.target.value); setErrors((p) => ({ ...p, firstName: '' })); }} className={`${glassInput} ${errors.firstName ? 'border-destructive' : ''}`} />
              {errors.firstName && <p className="text-[11px] text-destructive">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm text-foreground">{t.last_name || 'Last Name'}</Label>
              <Input id="lastName" type="text" placeholder={t.last_name || 'Last name'} value={lastName} onChange={(e) => { setLastName(e.target.value); setErrors((p) => ({ ...p, lastName: '' })); }} className={`${glassInput} ${errors.lastName ? 'border-destructive' : ''}`} />
              {errors.lastName && <p className="text-[11px] text-destructive">{errors.lastName}</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-foreground">{t.email}</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: '' })); }} className={`${glassInput} ${errors.email ? 'border-destructive' : ''}`} autoComplete="email" />
            {errors.email && <p className="text-[11px] text-destructive">{errors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-foreground">{t.password}</Label>
            <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: '' })); }} className={`${glassInput} ${errors.password ? 'border-destructive' : ''}`} autoComplete="new-password" />
            {errors.password && <p className="text-[11px] text-destructive">{errors.password}</p>}
          </div>
          <label className="flex items-start gap-2 pt-1 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={acceptedPrivacy} onChange={(e) => { setAcceptedPrivacy(e.target.checked); setErrors((p) => ({ ...p, privacy: '' })); }} className="mt-0.5 h-4 w-4 accent-primary" />
            <span>
              {language === 'fr' ? "J'accepte la " : 'I accept the '}
              <button type="button" onClick={() => navigate('/privacy')} className="font-semibold text-primary hover:underline">
                {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
              </button>
            </span>
          </label>
          {errors.privacy && <p className="text-[11px] text-destructive">{errors.privacy}</p>}
          <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => { setAcceptedTerms(e.target.checked); setErrors((p) => ({ ...p, terms: '' })); }} className="mt-0.5 h-4 w-4 accent-primary" />
            <span>
              {language === 'fr' ? "J'accepte les " : 'I accept the '}
              <button type="button" onClick={() => navigate('/terms')} className="font-semibold text-primary hover:underline">
                {language === 'fr' ? "Conditions Générales d'Utilisation" : 'Terms of Use'}
              </button>
            </span>
          </label>
          {errors.terms && <p className="text-[11px] text-destructive">{errors.terms}</p>}
          <div className="pt-1">
            <div className="relative">
              <div className="absolute inset-0 rounded-xl bg-primary/25 blur-lg" />
              <Button type="submit" disabled={submitting} className="relative w-full rounded-xl bg-primary py-6 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 active:scale-[0.97] transition-transform disabled:opacity-60">
                {submitting ? (language === 'fr' ? 'Création…' : 'Creating…') : t.create_account}
              </Button>
            </div>
          </div>
        </form>
        <AppleSignInButton label={language === 'fr' ? "S'inscrire avec Apple" : 'Sign up with Apple'} />
        <p className="mt-3 text-center text-[11px] leading-relaxed text-muted-foreground">
          {language === 'fr'
            ? "En continuant avec Apple, tu acceptes les Conditions Générales d'Utilisation (tolérance zéro envers les contenus abusifs) et la Politique de confidentialité de NextChamp."
            : 'By continuing with Apple, you accept the NextChamp Terms of Use (zero tolerance for abusive content) and Privacy Policy.'}
        </p>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.have_account}{' '}
          <button onClick={() => navigate('/login')} className="font-semibold text-primary hover:underline">{t.log_in}</button>
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <button onClick={() => navigate('/privacy')} className="underline-offset-2 hover:text-foreground hover:underline">
            {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
          </button>
          <span className="opacity-50">·</span>
          <button onClick={() => navigate('/terms')} className="underline-offset-2 hover:text-foreground hover:underline">
            {language === 'fr' ? "Conditions d'Utilisation" : 'Terms of Use'}
          </button>
          <span className="opacity-50">·</span>
          <button onClick={() => navigate('/legal')} className="underline-offset-2 hover:text-foreground hover:underline">
            {language === 'fr' ? 'Mentions légales' : 'Legal Notice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
